/**
 * Composable for filtering and sorting wishlist items
 * @param {Array} items - Array of wishlist items to filter and sort
 * @param {Array} selectedFilters - Array of filter keys to apply
 * @param {string} sortValue - Sort criteria
 * @param {Object} filterChecks - Custom filter check functions
 * @returns {Array} Filtered and sorted array of items
 */
export default function useWishlistFilterSort(items = [], selectedFilters = [], sortValue = '', filterChecks = {}) {
  if (!Array.isArray(items)) {
    console.warn('useWishlistFilterSort: Expected items to be an array, received:', typeof items);
    return [];
  }

  // Create a shallow copy to avoid mutating the original array
  let filtered = [...items];

  // Define default filter checks
  const defaultChecks = {
    'in-stock': (item) => item.upc?.availability?.available ?? true,
    'on-sale': (item) => item.upc?.price?.onSale ?? false,
    'promotional-offers': (item) => item.promotions?.length ?? false,
  };

  // Merge default checks with any custom checks
  const checks = { ...defaultChecks, ...filterChecks };

  // Apply filters if any are selected
  if (Array.isArray(selectedFilters) && selectedFilters.length > 0) {
    filtered = filtered.filter((item) => selectedFilters.every((filter) => (typeof checks[filter] === 'function' ? checks[filter](item) : true)));
  }

  // Apply sorting if sortValue is provided
  if (sortValue) {
    filtered = sortItems(filtered, sortValue);
  }

  return filtered;
}

/**
 * Sorts items based on the specified sort value
 * @param {Array} items - Items to sort
 * @param {string} sortValue - Sort criteria
 * @returns {Array} Sorted array of items
 */
function sortItems(items, sortValue) {
  const sorters = {
    RECENTLY_ADDED: (a, b) => new Date(b.addedDate || 0) - new Date(a.addedDate || 0),

    PRICE_LOW_TO_HIGH: (a, b) => getPrice(a) - getPrice(b),

    PRICE_HIGH_TO_LOW: (a, b) => getPrice(b) - getPrice(a),

    CUSTOMER_TOP_RATED: (a, b) => {
      const ratingA = a.upc?.product?.reviewStatistics?.averageRating || 0;
      const ratingB = b.upc?.product?.reviewStatistics?.averageRating || 0;

      if (ratingA === ratingB) {
        const reviewCountA = a.upc?.product?.reviewStatistics?.reviewCount || 0;
        const reviewCountB = b.upc?.product?.reviewStatistics?.reviewCount || 0;
        return reviewCountB - reviewCountA;
      }
      return ratingB - ratingA;
    },
  };

  const sorter = sorters[sortValue];
  return sorter ? [...items].sort(sorter) : items;
}

/**
 * Helper to get the display price of an item
 * @param {Object} item - Wishlist item
 * @returns {number} Price value
 */
function getPrice(item) {
  return item.upc?.price?.retailPrice || item.upc?.price?.originalPrice || 0;
}
